import DiamondIcon from '@app/assets/icons/diamond.svg?react';
import EnterpriseIcon from '@app/assets/icons/enterprise.svg?react';
import AnalyticsIcon from '@app/assets/icons/menu/analytics.svg?react';
import AutomationsIcon from '@app/assets/icons/menu/automations.svg?react';
import CampaignsIcon from '@app/assets/icons/menu/campaigns.svg?react';
import CustomersIcon from '@app/assets/icons/menu/customers.svg?react';
import RatingsIcon from '@app/assets/icons/menu/ratings.svg?react';
import IntegrationsIcon from '@app/assets/icons/menu/integrations.svg?react';
import DashboardIcon from '@app/assets/icons/menu/dashboard.svg?react';
import OnboardingIcon from '@app/assets/icons/menu/onboarding.svg?react';
import BriefcaseIcon from '@app/assets/icons/briefcase.svg?react';
import SettingsIcon from '@app/assets/icons/menu/settings.svg?react';
import OpenMailIcon from '@app/assets/icons/open-mail.svg?react';
import StarIcon from '@app/assets/icons/star.svg?react';
import SeatsIcon from '@app/assets/icons/seats.svg?react';
import AutomationIcon from '@app/assets/icons/automation.svg?react';
import BulkIcon from '@app/assets/icons/bulk-import.svg?react';
import MessageIcon from '@app/assets/icons/message.svg?react';
import TemplatesIcon from '@app/assets/icons/menu/templates.svg?react';
import type { EmailDesignContent, MessageDesignContent } from '@app/models/messageTemplates';

export interface SideMenuItem {
  label: string;
  to: string;
  Icon?: React.FunctionComponent<
    React.SVGProps<SVGSVGElement> & {
      title?: string;
    }
  >;
  children?: SideMenuItem[];
  comingSoon?: boolean;
}

export enum MembershipTypes {
  Basic = 'BASIC',
  Essential = 'ESSENTIAL',
  Professional = 'PROFESSIONAL',
  Enterprise = 'ENTERPRISE',
  Free = 'FREE',
  Pro = 'PRO',
  Growth = 'GROWTH',
}

export enum CustomerStatus {
  NO_STATUS = 'null',
  REVIEWD = 'reviewed',
  CAMPAIGN_SENT = 'campaign_sent',
  FEEDBACK = 'feedback',
  UNSUBSCRIBED = 'unsubscribed',
}

export enum CAMPAIGN_STATUS {
  SCHEDULED = 'scheduled',
  ACTIVE = 'active',
  COMPLETED = 'completed',
  CANCELLED = 'cancelled',
  DRAFTS = 'drafts',
}

export enum SettingTabs {
  DETAILS = 'details',
  MEMBERS = 'members',
  PAYMENT_METHODS = 'payment-methods',
  PLAN = 'plan',
}

export enum CreateCampaign {
  SETTINGS = 'settings',
  CUSTOMERS = 'customers',
  CAMPAIGN = 'campaign',
}

export enum CreateAutomation {
  SETTINGS = 'settings',
  AUTOMATION = 'automation',
}

export enum MessageTemplateChannel {
  EMAIL = 'EMAIL',
  SMS = 'SMS',
}

export const MessageTemplateChannelLabels: Record<MessageTemplateChannel, string> = {
  [MessageTemplateChannel.EMAIL]: 'Email',
  [MessageTemplateChannel.SMS]: 'SMS',
};

// Initial content of the email/SMS design editors, kept in sync with the backend `message-template-defaults.ts`
export const DEFAULT_EMAIL_DESIGN_CONTENT: EmailDesignContent = {
  subject: 'How was your experience?',
  heading: 'How was your experience?',
  message: `Hi {{customer-first-name}},
At {{business-name}}, we're always working to improve our product service, and we need your help to do just that.
If you have a moment, please leave us a review by clicking below.`,
  buttonText: 'Share feedback',
  buttonColor: '#3b82f6',
  logoUrl: '',
};

export const DEFAULT_MESSAGE_DESIGN_CONTENT: MessageDesignContent = {
  logoUrl: '',
  previewName: 'How was your experience?',
  message: 'Hi, we hope you enjoyed our service! Your feedback matters to us. Please take a moment to share your thoughts and help us improve. Click {{buttonUrl}}. Thank you! - {{business-name}}',
};

export const CustomerStatusMap = new Map([
  [CustomerStatus.NO_STATUS, { label: 'No status', color: 'bg-base-coolGray' }],
  [CustomerStatus.REVIEWD, { label: 'Reviewed', color: 'bg-system-success' }],
  [
    CustomerStatus.CAMPAIGN_SENT,
    { label: 'Campaign sent', color: 'bg-brand-baseBlue' },
  ],
  [CustomerStatus.FEEDBACK, { label: 'Feedback', color: 'bg-system-warning' }],
  [
    CustomerStatus.UNSUBSCRIBED,
    { label: 'Unsubscribed', color: 'bg-system-error' },
  ],
]);

export const CampaignStatusMap = new Map([
  [
    CAMPAIGN_STATUS.SCHEDULED,
    { label: 'Scheduled', color: 'bg-brand-baseBlue' },
  ],
  [CAMPAIGN_STATUS.ACTIVE, { label: 'Active', color: 'bg-system-success' }],
  [
    CAMPAIGN_STATUS.COMPLETED,
    { label: 'Completed', color: 'bg-base-coolGray' },
  ],
  [CAMPAIGN_STATUS.CANCELLED, { label: 'Cancelled', color: 'bg-system-error' }],
  [CAMPAIGN_STATUS.DRAFTS, { label: 'Drafts', color: 'bg-base-coolGray' }],
]);

export const SettingsTabs = [
  { label: 'Business details', value: SettingTabs.DETAILS },
  { label: 'Team members', value: SettingTabs.MEMBERS },
  { label: 'Payment methods', value: SettingTabs.PAYMENT_METHODS },
  { label: 'Plan', value: SettingTabs.PLAN },
];

export const CustomerTabs = [
  { label: 'Profile Information', value: 1 },
  { label: 'Campaigns', value: 2 },
  { label: 'Activity', value: 3 },
];

export enum IntegrationType {
  YELP = 'YELP',
  GOOGLE = 'GOOGLE',
  TRUSTPILOT = 'TRUSTPILOT',
  FACEBOOK = 'FACEBOOK',
  JOBBER = 'JOBBER',
  SQUAREUP = 'SQUAREUP',
}

export const IntegrationLabels: Record<IntegrationType, string> = {
  [IntegrationType.YELP]: 'Yelp',
  [IntegrationType.GOOGLE]: 'Google',
  [IntegrationType.TRUSTPILOT]: 'Trustpilot',
  [IntegrationType.FACEBOOK]: 'Facebook',
  [IntegrationType.JOBBER]: 'Jobber',
  [IntegrationType.SQUAREUP]: 'SquareUp',
};

export const availableIntegrations = [
  IntegrationType.YELP,
  IntegrationType.GOOGLE,
  IntegrationType.JOBBER,
  IntegrationType.SQUAREUP,
];



export const availableIntegrationsReviews = [
  IntegrationType.GOOGLE,
];

export const availableIntegrationsCrm = [
  IntegrationType.JOBBER,
  IntegrationType.SQUAREUP,

  // IntegrationType.YELP,
  // IntegrationType.GOOGLE,
];

export const nangoIntegrations = [
  IntegrationType.JOBBER,
  IntegrationType.SQUAREUP,
];

export const planNames = [
  {
    name: MembershipTypes.Free,
    price: 0,
    Icon: BriefcaseIcon,
    class: 'blue-to-pink',
    color: '[&_path]:stroke-blue-500',
  },
  {
    name: MembershipTypes.Pro,
    price: 97,
    Icon: DiamondIcon,
    class: 'blue-to-green',
    color: '[&_path]:stroke-[#4ada73]',
  },
  {
    name: MembershipTypes.Growth,
    price: 197,
    Icon: EnterpriseIcon,
    class: 'blue-to-dark-blue',
    color: '[&_path]:stroke-[#52b6fe]',
  },
];

export const plansAndBenefits = [
  {
    title: 'Member seats',
    values: [1, 20, 50],
    plansWith: [
      MembershipTypes.Basic,
      MembershipTypes.Professional,
      MembershipTypes.Enterprise,
    ],
  },
  {
    title: 'Outreach Attempts (up to)',
    values: [300, 10000, 50000],
    plansWith: [
      MembershipTypes.Basic,
      MembershipTypes.Professional,
      MembershipTypes.Enterprise,
    ],
  },
  {
    title: 'Synchronization with leading review platforms',
    values: [true, true, true],
    plansWith: [
      MembershipTypes.Basic,
      MembershipTypes.Professional,
      MembershipTypes.Enterprise,
    ],
  },
  {
    title: 'Review outreach/follow up via SMS/email',
    values: [true, true, true],
    plansWith: [
      MembershipTypes.Basic,
      MembershipTypes.Professional,
      MembershipTypes.Enterprise,
    ],
  },
  {
    title:
      'Guide customers to leaving reviews or non public feedback\n to help your business improve',
    values: [true, true, true],
    plansWith: [
      MembershipTypes.Basic,
      MembershipTypes.Professional,
      MembershipTypes.Enterprise,
    ],
  },
  {
    title: 'Manage and reply to all business reviews from one place',
    values: [true, true, true],
    plansWith: [
      MembershipTypes.Basic,
      MembershipTypes.Professional,
      MembershipTypes.Enterprise,
    ],
  },
  {
    title: 'Bulk import customers',
    values: [true, true, true],
    plansWith: [
      MembershipTypes.Basic,
      MembershipTypes.Professional,
      MembershipTypes.Enterprise,
    ],
  },
  {
    title: 'Customer profile management for organization',
    values: [true, true, true],
    plansWith: [
      MembershipTypes.Basic,
      MembershipTypes.Professional,
      MembershipTypes.Enterprise,
    ],
  },
  {
    title: 'Custom review outreach campaigns',
    values: [true, true, true],
    plansWith: [
      MembershipTypes.Basic,
      MembershipTypes.Professional,
      MembershipTypes.Enterprise,
    ],
  },
  {
    title: 'AI Assisted Customer Reviews',
    values: [false, true, true],
    plansWith: [MembershipTypes.Professional, MembershipTypes.Enterprise],
  },
  {
    title: 'AI Assisted Responses',
    values: [false, true, true],
    plansWith: [MembershipTypes.Professional, MembershipTypes.Enterprise],
  },
  {
    title: 'AI Assisted Review Outreach and Follow up',
    values: [false, true, true],
    plansWith: [MembershipTypes.Professional, MembershipTypes.Enterprise],
  },
  {
    title: 'CRM synchronization',
    values: [false, true, true],
    plansWith: [MembershipTypes.Professional, MembershipTypes.Enterprise],
    style: 'italic',
  },
  {
    title: 'Basic review analytics',
    values: [false, true, true],
    plansWith: [MembershipTypes.Professional, MembershipTypes.Enterprise],
  },
  {
    title: 'Advanced review analytics',
    values: [false, true, true],
    plansWith: [MembershipTypes.Professional, MembershipTypes.Enterprise],
  },
  {
    title: '24/7 support line for Enterprise customers',
    values: [false, false, true],
    plansWith: [MembershipTypes.Enterprise],
    style: 'italic',
  },
  {
    title: 'Desk Dedicated success agent',
    values: [false, false, true],
    plansWith: [MembershipTypes.Enterprise],
    style: 'italic',
  },
];

export const sideDrawerItems: SideMenuItem[] = [
  {
    label: 'Onboarding',
    to: '/onboarding',
    Icon: OnboardingIcon,
  },
  {
    label: 'Dashboard',
    to: '/',
    Icon: DashboardIcon,
  },
  {
    label: 'Ratings',
    to: '/ratings',
    Icon: RatingsIcon,
    children: [
      { label: 'Reviews', to: '/ratings/reviews' },
      { label: 'Feedbacks', to: '/ratings/feedbacks' },
    ],
  },
  {
    label: 'Customers',
    to: '/customers',
    Icon: CustomersIcon,
    children: [
      { label: 'Contacts', to: '/customers/contacts' },
      { label: 'Lists', to: '/customers/lists' },
      { label: 'Tags', to: '/customers/tags' },
    ],
  },
  {
    label: 'Analytics',
    to: '/analytics',
    Icon: AnalyticsIcon,
    comingSoon: true,
  },
  {
    label: 'Templates',
    to: '/templates',
    Icon: TemplatesIcon,
  },
  {
    label: 'Campaigns',
    to: '/campaigns',
    Icon: CampaignsIcon,
  },
  {
    label: 'Automations',
    to: '/automations',
    Icon: AutomationsIcon,
    // comingSoon: true,
  },
  {
    label: 'Integrations',
    to: '/integrations',
    Icon: IntegrationsIcon,
    children: [
      { label: 'Review platforms', to: '/integrations/reviews' },
      // { label: 'CRM connection', to: '/integrations/crm', comingSoon: true },
      { label: 'CRM connection', to: '/integrations/crm' },
    ],
  },
  {
    label: 'Admin Panel',
    to: '/admin/businesses',
    Icon: SettingsIcon,
  },
];

export const ContactUsEmail = 'contact@echoratings.com';

export const plansFeatures = plansAndBenefits.map((plan) => plan.title);

export function comparePlans(plan1: MembershipTypes, plan2: MembershipTypes) {
  return plansAndBenefits.map((benefit) => {
    const plan1Index = planNames.findIndex(
      (plan: { name: string }) => plan.name === plan1
    );
    const plan2Index = planNames.findIndex((plan) => plan.name === plan2);

    if (benefit.values[plan1Index] === benefit.values[plan2Index]) {
      return null;
    }
    return {
      title: benefit.title,
      plan1Value: plan1Index !== -1 ? benefit.values[plan1Index] : null,
      plan2Value: plan2Index !== -1 ? benefit.values[plan2Index] : null,
      style: benefit.style,
    };
  });
}

export const BAD_REVIEW_REASONS = [
  {
    label: 'Navigation system',
    value: 'navigation_system',
  },
  {
    label: 'Unpleasant design',
    value: 'unpleasant_design',
  },
  {
    label: 'Limited features',
    value: 'limited_features',
  },
  {
    label: 'Technical issues',
    value: 'technical_issues',
  },
  {
    label: 'Lack of information',
    value: 'lack_of_information',
  },
  {
    label: 'Payment problems',
    value: 'payment_problems',
  },
  {
    label: 'Inadequate support',
    value: 'inadequate_support',
  },
  {
    label: 'Other',
    value: 'other',
  },
];

export const SubscriptionType = {
  BASIC: 'BASIC',
  PROFESSIONAL: 'PROFESSIONAL',
  ENTERPRISE: 'ENTERPRISE',
  FREE: 'FREE',
  PRO: 'PRO',
  GROWTH: 'GROWTH',
} as const;

export type SubscriptionTypeValue =
  (typeof SubscriptionType)[keyof typeof SubscriptionType];

/**
 * Filters sidebar menu items based on user plan restrictions
 * @param planType - The current user's plan type (BASIC, PROFESSIONAL, ENTERPRISE)
 * @returns Filtered menu items array
 */
export const getFilteredSideDrawerItems = (
  planType?: string,
  isAdmin?: boolean
): SideMenuItem[] => {
  return sideDrawerItems
    .filter((item) => {
      // Hide Analytics menu if plan is BASIC
      if (item.label === 'Analytics' && planType === 'BASIC') {
        return false;
      }
      // Hide Admin Panel if user is not admin
      if (item.label === 'Admin Panel' && !isAdmin) {
        return false;
      }
      return true;
    })
    .map((item) => {
      // Handle Analytics submenu filtering for PROFESSIONAL plan
      if (item.label === 'Analytics' && planType === 'PROFESSIONAL') {
        const filteredChildren = item.children?.filter(
          (child) => child.label !== 'Ratings'
        );
        return {
          ...item,
          children: filteredChildren,
        };
      }
      return item;
    });
};

// Business category → subcategories mapping used in Settings → Business Details
export const CATEGORY_SUBCATEGORIES: Record<string, string[]> = {
  'Legal & Financial': [
    'Law Firm',
    'Accountant (CPA)',
    'Financial Advisor',
    'Insurance Agency',
    'Tax Services',
  ],
  'Health & Medicine': [
    'Clinic',
    'Dentist',
    'Pharmacy',
    'Chiropractor',
    'Optometrist',
  ],
  'Construction & Contractors': [
    'General Contractor',
    'Electrician',
    'Plumber',
    'HVAC',
    'Roofing',
  ],
  Education: [
    'School',
    'Tutoring Center',
    'Training Center',
    'Music School',
    'Early Education',
  ],
  Automotive: [
    'Auto Repair',
    'Car Dealership',
    'Car Wash',
    'Auto Parts',
    'Detailing',
  ],
  'Computers & Electronics': [
    'Software Development',
    'Cybersecurity Solutions',
    'Artificial Intelligence Services',
    'IT Consulting',
    'Cloud Computing Platforms',
  ],
  'Food & Dining': ['Restaurant', 'Cafe', 'Bakery', 'Catering', 'Food Truck'],
  Entertainment: [
    'Event Venue',
    'Movie Theater',
    'Gaming Center',
    'Music & Dance',
    'Bowling Alley',
  ],
};

// Features displayed in Welcome, TrialWelcome, WelcomeExisting, and SignUp pages
export const WELCOME_FEATURES = [
  {
    icon: <OpenMailIcon />,
    title: 'Review Outreach & Follow-Up via SMS / Email',
    description:
      'Easily request reviews from your customers across popular channels.',
  },
  {
    icon: <MessageIcon />,
    title: 'Centralized Review Management',
    description:
      'View, manage, and reply to reviews from one unified dashboard.',
  },
  {
    icon: <StarIcon />,
    title: 'Sync with Top Review Platforms',
    description:
      'Connect with platforms like Google, Yelp, Facebook, and more.',
  },
  {
    icon: <AutomationIcon />,
    title: 'Unlimited Review Monitoring & Response Management',
    description: 'Reply to customer reviews from one unified dashboard.',
  },
  {
    icon: <SeatsIcon />,
    title: 'Hands-On Support for Setup & Best Practices',
    description:
      "We don't leave you guessing — every plan gets guided onboarding help.",
  },
  {
    icon: <BulkIcon />,
    title: 'No Contracts — Cancel Anytime',
    description: 'Full flexibility as your business evolves.',
  },
];
